import { useCallback, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { runPollInterval, statusForPendingRun, useRunStatusQuery } from './useRunStatusQuery';
import { startRunWithDeadline } from '../utils/runStartDeadline';
import { batchStatus, hasAllBatchRows } from '../components/BulkUpload/utils/batchStatus';
import {
  startAssignmentExtraction,
} from '../service';
import {
  extractedCount,
  extractionSummaryMessage,
  failedExtractionSubmissionIds,
  firstExtractedRow,
  isExtractionActive,
  isExtractionSettledRun,
  settledStudentIds,
  stillConvertingCount,
} from '../components/BulkUpload/utils/extractionRun';

export const EXTRACTION_RUN_POLL_MS = 2500;

export const extractionPollInterval = (status, state = {}) =>
  runPollInterval(status, state, isExtractionActive, state.isRunRequested);

/**
 * Its own key, NOT the feedback run's.
 *
 * This read asks for includeText, and the feedback poll must not start paying for
 * 200 students of extracted text every 2.5 seconds — which is the whole reason
 * getAssignmentSubmissionsStatus makes includeText opt-in.
 */
export const assignmentExtractionRunKey = (assignmentId) => [
  'assignmentExtractionRun',
  assignmentId,
];

/**
 * One POST for the whole assignment, then one poll that stops on its own.
 *
 * The fan-out this replaces lived in the browser: one request per student, each
 * carrying an attachment generation and its own 409-refetch retry, six at a time.
 * A 50-response batch spent the poll budget on the kicks alone.
 *
 * Called from BulkUploadFlow rather than from the Converting step, because the
 * poll has to outlive it: Converting leaves on the FIRST student to extract, and
 * Review reads the same live status to decide which rows are still locked.
 */
export const useAssignmentExtractionRun = (assignmentId, { enabled = true, submissionIds } = {}) => {
  const requestScope = useRef(null);
  if (!requestScope.current || requestScope.current.assignmentId !== assignmentId) requestScope.current = { assignmentId, attempt: null };
  const scope = requestScope.current;
  const startMutation = useMutation({
    mutationFn: ({ target, submissionIds }) => startRunWithDeadline(signal => startAssignmentExtraction(target, { submissionIds, signal })),
    retry: false,
    networkMode: 'always',
    onSuccess: async (_receipt, { owner }) => {
      if (owner !== requestScope.current) return;
      await statusQuery.refreshAfterStart();
      owner.attempt.awaitingRead = false;
    },
  });
  const isStarting = startMutation.variables?.owner === scope && startMutation.isLoading;
  const startError = startMutation.variables?.owner === scope ? startMutation.error : null;
  const statusQuery = useRunStatusQuery({ assignmentId, queryKey: assignmentExtractionRunKey(assignmentId),
    enabled, includeText: true, isRunRequested: isStarting, isActive: isExtractionActive });
  const { data: assignmentStatus, error: statusError, refetch: refetchStatus } = statusQuery;
  const attempt = scope.attempt;
  const targetedIds = attempt?.submissionIds ?? submissionIds;
  const status = useMemo(() => batchStatus(
    attempt?.awaitingRead ? statusForPendingRun(assignmentStatus, attempt.baseline, 'extraction', attempt.observed) : assignmentStatus,
    targetedIds), [assignmentStatus, targetedIds, attempt, isStarting]);

  const start = useCallback((options = {}) => {
    if (!assignmentId) return Promise.reject(new Error('Extraction cannot start before the task is saved.'));
    if (scope.promise) return scope.promise;
    scope.attempt = { baseline: assignmentStatus, submissionIds: options.submissionIds ?? submissionIds, awaitingRead: true, observed: new Map() };
    const pending = startMutation.mutateAsync({ target: assignmentId, submissionIds: scope.attempt.submissionIds, owner: scope });
    scope.promise = pending;
    const clear = () => { if (scope.promise === pending) scope.promise = null; };
    pending.then(clear, clear);
    return pending;
  }, [scope, assignmentId, assignmentStatus, submissionIds, startMutation]);

  const derived = useMemo(
    () => ({
      message: extractionSummaryMessage(status),
      extracted: extractedCount(status),
      stillConverting: stillConvertingCount(status),
      firstExtractedRow: statusError || startError ? null : firstExtractedRow(status),
      // The gate on the save at Continue: a student still extracting must not
      // have the client's idea of their text written over the answer.
      settledStudentIds: settledStudentIds(status),
      failedSubmissionIds: failedExtractionSubmissionIds(status),
      isSettled: !isStarting && !attempt?.awaitingRead && !statusError && hasAllBatchRows(status, targetedIds) && isExtractionSettledRun(status),
      isActive: isExtractionActive(status),
    }),
    [status, targetedIds, isStarting, statusError, startError, attempt],
  );

  return {
    start,
    refetchStatus,
    status,
    statusError,
    startError,
    isStarting,
    ...derived,
  };
};
